import type { StyleRule } from '@archipro-design/aria';
import {
    captionClassName,
    SubheaderClassName,
    pxArrayToRem,
    pxToRem,
    buttonClassName,
} from '@archipro-design/aria';

const rowStyleDefault: StyleRule = ({ theme }) => ({
    display: 'inline-block',
    fontSize: theme.siteVariables.fontSizes.caption03,
    letterSpacing: 0,
    lineHeight: 1.1,
    marginBottom: pxToRem(7),

    maxWidth: pxToRem(378),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    [`&.${captionClassName}`]: {
        lineHeight: 1.1,
    },

    [theme.screen.laptop]: {
        lineHeight: pxToRem(24),
    },
});

const rowStyleDefaultDesktop: StyleRule = ({ theme }) => ({
    lineHeight: theme.siteVariables.lineHeightDefault,
    fontSize: pxToRem(16),
    maxWidth: pxToRem(500),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    paddingRight: pxToRem(16),
});

export const RowDate: StyleRule = ({ theme }) => ({
    ...rowStyleDefault({ theme }),
    [theme.screen.laptop]: {
        ...rowStyleDefaultDesktop({ theme }),
    },
});

export const RowOrgName: StyleRule = ({ theme }) => ({
    ...rowStyleDefault({ theme }),
    width: '100%',
    display: 'block',

    [`&.${SubheaderClassName}`]: {
        display: 'flex',
        justifyContent: 'flexEnd',

        fontWeight: theme.siteVariables.fontWeightRegular,
    },

    [theme.screen.laptop]: {
        ...rowStyleDefaultDesktop({ theme }),
        maxWidth: pxToRem(480),
        [`&.${SubheaderClassName}`]: {
            display: 'block',
        },
    },
});

export const RowAwardName: StyleRule = ({ theme }) => ({
    ...rowStyleDefault({ theme }),
    display: 'block',

    [`&.${captionClassName}`]: {
        fontWeight: theme.siteVariables.fontWeightRegular,
        marginBottom: pxToRem(7),
        lineHeight: 1.1,
    },

    [theme.screen.laptop]: {
        ...rowStyleDefaultDesktop({ theme }),
        maxWidth: pxToRem(740),
        overflow: 'hidden',
        whiteSpace: 'normal',
        lineHeight: 1.5,
    },
});

export const RowAwardEntity: StyleRule = ({ theme }) => ({
    ...rowStyleDefault({ theme }),
    width: '100%',
    display: 'block',

    [`&.${captionClassName}`]: {
        fontWeight: theme.siteVariables.fontWeightRegular,
        marginBottom: pxToRem(7),
    },

    [theme.screen.laptop]: {
        ...rowStyleDefaultDesktop({ theme }),
        marginBottom: 0,
        display: 'grid',
        rowGap: pxToRem(13),
    },
});

export const RowItemLink: StyleRule = () => ({
    width: '90%',
});

export const RowItem: StyleRule = ({ theme }) => ({
    borderBottom: `1px solid ${theme.siteVariables.colors.primitive.black}`,
    paddingTop: pxToRem(20),
    paddingBottom: pxToRem(20),
    display: 'grid',
    gridTemplateColumns: `${pxToRem(62)} 1fr`,
    justifyContent: 'end',

    [theme.screen.laptop]: {
        gridTemplateColumns: `${pxToRem(260)} ${pxToRem(500)} 1fr ${pxToRem(
            400
        )}`,
        borderBottom: `1px solid ${theme.siteVariables.colors.primitive.black}`,
    },
});

export const DirectoryItemLink: StyleRule = ({ theme }) => ({
    textTransform: 'uppercase',
    color: theme.siteVariables.colors.charcoal[250],
    fontSize: pxToRem(16),
    textDecoration: 'underline',
    maxHeight: pxToRem(24),
    marginBottom: pxToRem(7),
    width: '100%',

    textOverflow: 'ellipsis',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',

    whiteSpace: 'pre',
    justifyContent: 'end',

    [theme.screen.laptop]: {
        display: 'block',
        marginBottom: pxToRem(13),
        width: pxToRem(310),
        '&:last-child': {
            marginBottom: 0,
        },
        '&:-webkit-any-link': {
            textDecoration: 'underline',
        },
    },
});

//packages/aria/src/themes/base/staticStyles/globalStyles.ts
//override underlined being set to none in global style
export const UnderlinedLinkTitle: StyleRule = () => ({
    textDecoration: 'underline',

    wordWrap: 'break-word',
    whiteSpace: 'pre',
    maxHeight: pxToRem(24),

    maxWidth: pxToRem(300),
    position: 'relative',
    display: 'block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
});

export const MultipleRowMobile: StyleRule = () => ({
    paddingBottom: pxToRem(20),
    paddingTop: pxToRem(20),
    marginBottom: pxToRem(1),
});

export const RowSingle: StyleRule = () => ({
    gridTemplateColumns: 'unset',
});

export const RowSingleMobile: StyleRule = ({ theme }) => ({
    gridTemplateColumns: 'unset',

    lineHeight: pxToRem(14),
    paddingTop: pxToRem(22),
    paddingBottom: pxToRem(22),
    marginBottom: pxToRem(1),
    letterSpacing: pxToRem(0),
    fontWeight: theme.siteVariables.textStyles.Text.Medium65.fontWeight,
});

export const AwardTable: StyleRule = ({ theme }) => ({
    maxWidth: pxToRem(414),
    padding: pxArrayToRem([0, 18]),
    paddingBottom: pxToRem(17),
    display: 'grid',

    [theme.screen.laptop]: {
        maxWidth: 'unset',
        padding: 0,
        borderTop: `1px solid ${theme.siteVariables.colors.primitive.black}`,
    },
});

export const AwardWrapper: StyleRule = ({ theme }) => ({
    [theme.screen.laptop]: {
        marginBottom: 0,
        marginLeft: pxToRem(-6),
        marginRight: pxToRem(6),
    },
    [`& .${buttonClassName}`]: {
        width: 'fit-content',
        margin: 'auto',
    },
});

export const HeaderTitleMobile: StyleRule = ({ theme }) => ({
    margin: pxArrayToRem([0, 18, 25, 18]),
    fontSize: pxToRem(16),
    lineHeight: pxToRem(16),
    letterSpacing: theme.siteVariables.letterSpacingSmall,
    textTransform: 'uppercase',
    ...theme.siteVariables.textStyles.Heading.Medium65,
    fontWeight: '500',
});

export const AwardContainerMobile: StyleRule = ({ theme }) => ({
    textAlign: 'end',
    borderBottom: `1px solid ${theme.siteVariables.colors.primitive.black}`,
    paddingBottom: pxToRem(5),
    marginBottom: pxToRem(12),
});
